import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, map, mergeMap, withLatestFrom } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { DataActions } from "./data.actions";
import { selectUserContext, selectSelectedCompany } from "../../user/user.selectors";
import { UserActions } from "../../user/user.actions";
import { normalizeError } from "../../app-store-error.types";

// Feature actions
import { DataCurrenciesActions } from "./currency/currencies/currencies.actions";
import { DataDeliveryTermsActions } from "./delivery-term/delivery-terms/delivery-terms.actions";
import { DataPaymentTermsActions } from "./payment-term/payment-terms/payment-terms.actions";
import { DataInterestRatesActions } from "./interest-rate/interest-rates/interest-rates.actions";
import { DataFinancingCompaniesActions } from "./financing-company/financing-companies/financing-companies.actions";
import { DataSalesCreditRolesActions } from "./sales-credit-role/sales-credit-roles/sales-credit-roles.actions";
import { DataEcoLabelsActions } from "./eco-label/eco-labels/eco-labels.actions";
import { DataUnspscsActions } from "./unspsc/unspscs/unspscs.actions";
import { DataBrandsActions } from "./brand/brands/brands.actions";
import { DataCategoriesActions } from "./category/categories/categories.actions";
import { DataLocationsActions } from "./location/locations/locations.actions";
import { DataContractTypesActions } from "./contract-type/contract-types/contract-types.actions";
import { DataCatalogTypesActions } from "./catalog-type/catalog-types/catalog-types.actions";
import { DataSubscriptionConceptsActions } from "./subscription-concept/subscription-concepts/subscription-concepts.actions";
import { DataSalesPersonsActions } from "./sales-person/sales-persons/sales-persons.actions";
import { DataSalesSupportPersonsActions } from "./sales-support-person/sales-support-persons/sales-support-persons.actions";
import { CountriesActions } from "./country/countries/countries.actions";
import { DataCustomerTypesActions } from "./customer-type/customer-types/customer-types.actions";

// API clients
import {
    ContractClient,
    DeliveryTermsClient,
    PaymentTermsClient,
    ItemCategoryClient,
    CurrencyClient,
    CountryClient,
    LocationClient,
    FinancingCompaniesClient,
    SalesPersonClient,
    SalesSupportPersonClient,
    BrandClient,
    CatalogClient,
    SalesCreditRoleClient,
    EcoLabelClient,
    InterestRateClient,
    SubscriptionQuoteClient,
    UnspscClient,
    CustomerTypeClient,
} from "src/api-service";

@Injectable()
export class DataEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);

    // API clients
    private readonly contractClient = inject(ContractClient);
    private readonly deliveryTermsClient = inject(DeliveryTermsClient);
    private readonly paymentTermsClient = inject(PaymentTermsClient);
    private readonly categoryClient = inject(ItemCategoryClient);
    private readonly currencyClient = inject(CurrencyClient);
    private readonly countryClient = inject(CountryClient);
    private readonly locationClient = inject(LocationClient);
    private readonly financingCompaniesClient = inject(FinancingCompaniesClient);
    private readonly salesPersonClient = inject(SalesPersonClient);
    private readonly salesSupportPersonClient = inject(SalesSupportPersonClient);
    private readonly brandClient = inject(BrandClient);
    private readonly ecoLabelClient = inject(EcoLabelClient);
    private readonly unspscClient = inject(UnspscClient);
    private readonly interestRateClient = inject(InterestRateClient);
    private readonly subscriptionQuoteClient = inject(SubscriptionQuoteClient);
    private readonly catalogClient = inject(CatalogClient);
    private readonly salesCreditRoleClient = inject(SalesCreditRoleClient);
    private readonly customerTypeClient = inject(CustomerTypeClient);

    // =========================================================================
    // INITIALIZATION
    // =========================================================================

    // Trigger initialization when user is initialized
    triggerInitializeOnUserInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.initializeUserSuccess),
            map(({ selectedCompany }) => DataActions.initializeData({ companyId: selectedCompany.id })),
        ),
    );

    // Trigger initialization when company is changed
    triggerInitializeOnCompanyChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.changeCompanySuccess),
            map(({ company }) => DataActions.initializeData({ companyId: company.id })),
        ),
    );

    // Dispatch all feature load actions when InitializeData is triggered
    initializeData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataActions.initializeData),
            withLatestFrom(this.store.select(selectUserContext)),
            mergeMap(([{ companyId }, userContext]) => {
                const languageCode = userContext?.siteLanguageCode;

                return [
                    DataCurrenciesActions.load({ companyId }),
                    DataDeliveryTermsActions.load({ companyId, languageCode }),
                    DataPaymentTermsActions.load({ companyId, languageCode }),
                    DataInterestRatesActions.load(),
                    DataFinancingCompaniesActions.load({ companyId }),
                    DataSalesCreditRolesActions.load({ companyId, languageCode }),
                    DataEcoLabelsActions.load({ companyId }),
                    DataUnspscsActions.load({ companyId }),
                    DataBrandsActions.load({ companyId }),
                    DataCategoriesActions.load({ companyId }),
                    DataLocationsActions.load({ companyId, languageCode }),
                    DataContractTypesActions.load({ companyId, languageCode }),
                    DataCatalogTypesActions.load({ companyId }),
                    DataSubscriptionConceptsActions.load({ companyId }),
                    DataSalesPersonsActions.load({ companyId }),
                    DataSalesSupportPersonsActions.load({ companyId }),
                    CountriesActions.load(),
                    DataCustomerTypesActions.load({ companyId }),
                ];
            }),
        ),
    );

    // =========================================================================
    // FEATURE EFFECTS
    // =========================================================================

    loadCurrencies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataCurrenciesActions.load),
            mergeMap(({ companyId }) =>
                this.currencyClient.getAll(companyId).pipe(
                    map(response => DataCurrenciesActions.loadSuccess({ currencies: response ?? [] })),
                    catchError(error => of(DataCurrenciesActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_CURRENCIES_ERROR" }))),
                ),
            ),
        ),
    );

    loadDeliveryTerms$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataDeliveryTermsActions.load),
            mergeMap(({ companyId, languageCode }) =>
                this.deliveryTermsClient.getFiltered(companyId, 1, 1000, undefined, undefined, undefined, languageCode).pipe(
                    map(response => DataDeliveryTermsActions.loadSuccess({ deliveryTerms: response.data ?? [] })),
                    catchError(error => of(DataDeliveryTermsActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_DELIVERY_TERMS_ERROR" }))),
                ),
            ),
        ),
    );

    loadPaymentTerms$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataPaymentTermsActions.load),
            mergeMap(({ companyId, languageCode }) =>
                this.paymentTermsClient.getFiltered(companyId, 1, 1000, undefined, undefined, undefined, languageCode).pipe(
                    map(response => DataPaymentTermsActions.loadSuccess({ paymentTerms: response.data ?? [] })),
                    catchError(error => of(DataPaymentTermsActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_PAYMENT_TERMS_ERROR" }))),
                ),
            ),
        ),
    );

    loadInterestRates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataInterestRatesActions.load),
            mergeMap(() =>
                this.interestRateClient.getAll().pipe(
                    map(response => DataInterestRatesActions.loadSuccess({ interestRates: response })),
                    catchError(error => of(DataInterestRatesActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_INTEREST_RATES_ERROR" }))),
                ),
            ),
        ),
    );

    loadFinancingCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataFinancingCompaniesActions.load),
            mergeMap(({ companyId }) =>
                this.financingCompaniesClient.getFiltered(companyId, 1, 1000, undefined).pipe(
                    map(response => DataFinancingCompaniesActions.loadSuccess({ financingCompanies: response.data ?? [] })),
                    catchError(error => of(DataFinancingCompaniesActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_FINANCING_COMPANIES_ERROR" }))),
                ),
            ),
        ),
    );

    loadSalesCreditRoles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataSalesCreditRolesActions.load),
            mergeMap(({ companyId, languageCode }) =>
                this.salesCreditRoleClient.getAll(companyId, languageCode).pipe(
                    map(response => DataSalesCreditRolesActions.loadSuccess({ salesCreditRoles: response })),
                    catchError(error => of(DataSalesCreditRolesActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_SALES_CREDIT_ROLES_ERROR" }))),
                ),
            ),
        ),
    );

    loadEcoLabels$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataEcoLabelsActions.load),
            mergeMap(({ companyId }) =>
                this.ecoLabelClient.getAll(companyId).pipe(
                    map(response => DataEcoLabelsActions.loadSuccess({ ecoLabels: response })),
                    catchError(error => of(DataEcoLabelsActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_ECO_LABELS_ERROR" }))),
                ),
            ),
        ),
    );

    loadUnspscs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataUnspscsActions.load),
            mergeMap(({ companyId }) =>
                this.unspscClient.getAll(companyId).pipe(
                    map(response => DataUnspscsActions.loadSuccess({ unspscs: response })),
                    catchError(error => of(DataUnspscsActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_UNSPSCS_ERROR" }))),
                ),
            ),
        ),
    );

    loadBrands$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataBrandsActions.load),
            mergeMap(({ companyId }) =>
                this.brandClient.getAllBrands(companyId).pipe(
                    map(response => DataBrandsActions.loadSuccess({ brands: response })),
                    catchError(error => of(DataBrandsActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_BRANDS_ERROR" }))),
                ),
            ),
        ),
    );

    loadCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataCategoriesActions.load),
            mergeMap(({ companyId }) =>
                this.categoryClient.getItemCategories(companyId).pipe(
                    map(response =>
                        DataCategoriesActions.loadSuccess({
                            categories: response.map(category => ({
                                ...category,
                                name: this.translate.instant(`ITEM_CATEGORIES.${category.code}`) || category.name,
                            })),
                        }),
                    ),
                    catchError(error => of(DataCategoriesActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_CATEGORIES_ERROR" }))),
                ),
            ),
        ),
    );

    loadLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataLocationsActions.load),
            mergeMap(({ companyId, languageCode }) =>
                this.locationClient.getFiltered(companyId, 1, 1000, undefined, undefined, undefined, languageCode).pipe(
                    map(response => DataLocationsActions.loadSuccess({ locations: response.data ?? [] })),
                    catchError(error => of(DataLocationsActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_LOCATIONS_ERROR" }))),
                ),
            ),
        ),
    );

    loadContractTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataContractTypesActions.load),
            mergeMap(({ companyId, languageCode }) =>
                this.contractClient.getAllContractTypes(languageCode, companyId).pipe(
                    map(response => DataContractTypesActions.loadSuccess({ contractTypes: response })),
                    catchError(error => of(DataContractTypesActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_CONTRACT_TYPES_ERROR" }))),
                ),
            ),
        ),
    );

    loadCatalogTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataCatalogTypesActions.load),
            mergeMap(({ companyId }) =>
                this.catalogClient.getTypes(companyId).pipe(
                    map(response => DataCatalogTypesActions.loadSuccess({ catalogTypes: response })),
                    catchError(error => of(DataCatalogTypesActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_CATALOG_TYPES_ERROR" }))),
                ),
            ),
        ),
    );

    loadSubscriptionConcepts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataSubscriptionConceptsActions.load),
            mergeMap(({ companyId }) =>
                this.subscriptionQuoteClient.getConcepts(companyId).pipe(
                    map(response => DataSubscriptionConceptsActions.loadSuccess({ subscriptionConcepts: response })),
                    catchError(error => of(DataSubscriptionConceptsActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_SUBSCRIPTION_CONCEPTS_ERROR" }))),
                ),
            ),
        ),
    );

    loadSalesPersons$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataSalesPersonsActions.load),
            mergeMap(({ companyId }) =>
                this.salesPersonClient.getFiltered(companyId, 1, 1000, undefined, undefined, undefined, undefined, true).pipe(
                    map(response => DataSalesPersonsActions.loadSuccess({ salesPersons: response.data ?? [] })),
                    catchError(error => of(DataSalesPersonsActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_SALES_PERSONS_ERROR" }))),
                ),
            ),
        ),
    );

    loadSalesSupportPersons$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataSalesSupportPersonsActions.load),
            mergeMap(({ companyId }) =>
                this.salesSupportPersonClient.getFiltered(companyId, 1, 1000).pipe(
                    map(response => DataSalesSupportPersonsActions.loadSuccess({ salesSupportPersons: response.data ?? [] })),
                    catchError(error => of(DataSalesSupportPersonsActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_SALES_SUPPORT_PERSONS_ERROR" }))),
                ),
            ),
        ),
    );

    loadCountry$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CountriesActions.load),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            mergeMap(([_, selectedCompany]) =>
                this.countryClient.getAll().pipe(
                    map(countries => {
                        const userCountry = countries.find(c => c.code === selectedCompany?.countryCode);
                        return CountriesActions.loadSuccess({ country: userCountry ?? null });
                    }),
                    catchError(error => of(CountriesActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_COUNTRY_ERROR" }))),
                ),
            ),
        ),
    );

    loadCustomerTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DataCustomerTypesActions.load),
            mergeMap(({ companyId }) =>
                this.customerTypeClient.getAll(companyId).pipe(
                    map(response => DataCustomerTypesActions.loadSuccess({ customerTypes: response ?? [] })),
                    catchError(error => of(DataCustomerTypesActions.loadFailure({ error: normalizeError(error), toastKey: "DATA.TOAST.LOAD_CUSTOMER_TYPES_ERROR" }))),
                ),
            ),
        ),
    );

}
